import { createSlice, createAsyncThunk, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { AppState } from '../../app/store/store';
import { container } from '../../app/ioc/container';
import { TYPES } from '../../app/ioc/types';
import { DbManager } from '../../db/dbManager';
import PaymentEntity from '../../db/entities/payment/PaymentEntity';
import { PaymentDraft } from '../../db/entities/payment/mapper';
import {
  createPayment as apiCreatePayment,
  updatePayment as apiUpdatePayment,
  deletePayment as apiDeletePayment,
} from '../../api/payments/api';
import { selectCurrentWorkspaceId } from '../workspacesSlice';
import { refreshProformaInvoice } from '../proformaInvoicesSlice/proformaInvoicesSlice';
import { filterPaymentsByInvoice, sortPaymentsByReceivedDate } from './utils';

interface SliceState {
  list: PaymentEntity[];
}

const initialState: SliceState = {
  list: [],
};

// The list is fed from the local database (rxDbConnector), so API results are
// written there too; the stream would deliver them, but only on the next sync.
const saveToLocalDb = async (payment: PaymentEntity) => {
  const db = await container.get<DbManager>(TYPES.DbManager).getDb();
  await db.payments.bulkUpsert([payment]);
  return payment;
};

const removeFromLocalDb = async (uuid: string) => {
  const db = await container.get<DbManager>(TYPES.DbManager).getDb();
  await db.payments.bulkRemove([uuid]);
};

// A payment changes the invoice's total paid, balance and status on the server.
// The refresh is best effort: the stream delivers the same invoice update later.
const refreshInvoice = async (
  dispatch: (action: ReturnType<typeof refreshProformaInvoice>) => Promise<unknown>,
  invoiceId: string
) => {
  try {
    await dispatch(refreshProformaInvoice(invoiceId));
  } catch (error) {
    // Nothing to do; see above.
  }
};

export type NewPaymentDraft = Omit<PaymentDraft, 'uuid'>;

export const createPayment = createAsyncThunk<PaymentEntity, NewPaymentDraft, { state: AppState }>(
  'payments/create',
  async (draft, { getState, dispatch }) => {
    const workspaceId = selectCurrentWorkspaceId(getState());
    const payment = await saveToLocalDb(
      await apiCreatePayment({ ...draft, uuid: uuidv4() }, workspaceId)
    );
    await refreshInvoice(dispatch, payment.proformaInvoiceId);
    return payment;
  }
);

export const updatePayment = createAsyncThunk<PaymentEntity, PaymentDraft, { state: AppState }>(
  'payments/update',
  async (draft, { getState, dispatch }) => {
    const workspaceId = selectCurrentWorkspaceId(getState());
    const payment = await saveToLocalDb(await apiUpdatePayment(draft, workspaceId));
    await refreshInvoice(dispatch, payment.proformaInvoiceId);
    return payment;
  }
);

export const deletePayment = createAsyncThunk<
  string,
  Pick<PaymentEntity, 'uuid' | 'proformaInvoiceId'>,
  { state: AppState }
>('payments/delete', async ({ uuid, proformaInvoiceId }, { getState, dispatch }) => {
  const workspaceId = selectCurrentWorkspaceId(getState());
  await apiDeletePayment(uuid, workspaceId);
  await removeFromLocalDb(uuid);
  await refreshInvoice(dispatch, proformaInvoiceId);
  return uuid;
});

const upsert = (state: SliceState, payment: PaymentEntity) => {
  const index = state.list.findIndex((item) => item.uuid === payment.uuid);
  if (index >= 0) {
    state.list[index] = payment;
  } else {
    state.list.push(payment);
  }
};

const slice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    // Overwrite the entire list (from the local database)
    setPayments(state, action: PayloadAction<PaymentEntity[]>) {
      state.list = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createPayment.fulfilled, (state, action) => upsert(state, action.payload));
    builder.addCase(updatePayment.fulfilled, (state, action) => upsert(state, action.payload));
    builder.addCase(deletePayment.fulfilled, (state, action) => {
      state.list = state.list.filter((item) => item.uuid !== action.payload);
    });
  },
});

export const { setPayments } = slice.actions;
export const reducer = slice.reducer;
export default reducer;

export const selectPayments = (state: AppState): PaymentEntity[] => state.payments.list;

export const makeSelectPaymentsByInvoice = () =>
  createSelector(
    selectPayments,
    (_state: AppState, invoiceId: string) => invoiceId,
    (payments, invoiceId) =>
      sortPaymentsByReceivedDate(filterPaymentsByInvoice(payments, invoiceId))
  );
